import { useEffect, useState } from "react";
import { CheckCircle2, Star } from "lucide-react";
import { useLang } from "@/i18n";
import { useSEO } from "@/hooks/useSEO";
import { loadRooms } from "@/data/roomStore";
import { resolveRoom } from "@/data/rooms";
import { Reveal, ChapterHeading } from "@/components/Reveal";

const inputCls =
    "w-full rounded-xl border border-sand-border bg-white px-4 py-3 text-sm text-ink outline-none transition-colors duration-300 focus:border-olive focus:ring-2 focus:ring-olive/20";

export default function SurveyPage() {
    const { lang, t } = useLang();
    const [rating, setRating] = useState(0);
    const [sent, setSent] = useState(false);
    const [roomList, setRoomList] = useState([]);
    useSEO(t.survey.title, t.survey.desc);
    useEffect(() => {
        loadRooms().then(setRoomList).catch(() => {});
    }, []);

    return (
        <div className="pt-28 pb-24 md:pt-36 md:pb-32" data-testid="survey-page">
            <div className="mx-auto max-w-2xl px-6 md:px-12">
                <ChapterHeading label={t.survey.label} title={t.survey.heading} align="center" />
                <p className="mt-4 text-center text-base font-light leading-relaxed text-ink-muted">{t.survey.intro}</p>

                {sent ? (
                    <Reveal className="mt-12">
                        <div className="flex flex-col items-center gap-4 rounded-3xl bg-sand p-12 text-center" data-testid="survey-success">
                            <CheckCircle2 className="h-12 w-12 text-olive" strokeWidth={1.5} />
                            <p className="font-display text-2xl font-medium text-ink">{t.survey.thanks}</p>
                            <p className="text-sm font-light text-ink-muted">{t.survey.thanksSub}</p>
                        </div>
                    </Reveal>
                ) : (
                    <Reveal className="mt-12">
                        <form
                            className="space-y-5 rounded-3xl bg-sand p-7 shadow-[0_8px_30px_rgba(45,51,25,0.06)] md:p-10"
                            onSubmit={(e) => {
                                e.preventDefault();
                                setSent(true);
                            }}
                            data-testid="survey-form"
                        >
                            <div className="grid gap-5 sm:grid-cols-2">
                                <label className="block">
                                    <span className="mb-1.5 block text-xs font-medium text-ink-muted">{t.survey.name}</span>
                                    <input type="text" required className={inputCls} data-testid="survey-name-input" />
                                </label>
                                <label className="block">
                                    <span className="mb-1.5 block text-xs font-medium text-ink-muted">{t.survey.email}</span>
                                    <input type="email" required className={inputCls} data-testid="survey-email-input" />
                                </label>
                            </div>
                            <label className="block">
                                <span className="mb-1.5 block text-xs font-medium text-ink-muted">{t.survey.room}</span>
                                <select className={inputCls} data-testid="survey-room-select">
                                    {roomList.map((r) => (
                                        <option key={r.slug} value={r.slug}>
                                            {resolveRoom(r, lang).name}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            <div>
                                <span className="mb-2 block text-xs font-medium text-ink-muted">{t.survey.rating}</span>
                                <div className="flex gap-2" role="radiogroup" aria-label={t.survey.rating} data-testid="survey-rating">
                                    {[1, 2, 3, 4, 5].map((n) => (
                                        <button
                                            key={n}
                                            type="button"
                                            role="radio"
                                            aria-checked={rating === n}
                                            aria-label={`${n} estrellas`}
                                            onClick={() => setRating(n)}
                                            data-testid={`survey-star-${n}`}
                                            className="transition-transform duration-200 hover:scale-125"
                                        >
                                            <Star className={`h-8 w-8 ${n <= rating ? "fill-amber text-amber" : "text-sand-border"}`} strokeWidth={1.5} />
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <label className="block">
                                <span className="mb-1.5 block text-xs font-medium text-ink-muted">{t.survey.comments}</span>
                                <textarea rows="4" className={inputCls} data-testid="survey-comments-input" />
                            </label>
                            <button
                                type="submit"
                                data-testid="survey-submit-button"
                                className="w-full rounded-full bg-amber py-4 text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-amber-dark hover:shadow-lg"
                            >
                                {t.survey.send}
                            </button>
                        </form>
                    </Reveal>
                )}
            </div>
        </div>
    );
}
